import React, {
    useState,
    useEffect,
    forwardRef,
    useMemo,
    useRef,
    useImperativeHandle
} from 'react';
import { IFilterParams, IDoesFilterPassParams } from 'ag-grid-community';
import '../../assets/styles/checkbox-filter.css'; // Kita akan membuat file CSS ini di langkah berikutnya

// Interface tidak berubah
interface FilterOption {
    value: string;
    label: string;
}
interface ApiResponse {
    status: boolean;
    message: string;
    data: { id: string; name: string; }[];
}
interface FilterModel {
    values: string[];
}

const CheckboxFilterStatus = forwardRef((props: IFilterParams, ref) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    // 1. State baru untuk fungsionalitas tambahan
    const [searchTerm, setSearchTerm] = useState<string>('');
    const selectAllRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetch('https://api-kpa.ultimosolution.com/api/JobStatus?skip=0&take=50') // <--- GANTI URL INI
            .then(response => response.json())
            .then((result: ApiResponse) => {

                const hidenStatuses = [
                    "Complete MRE",
                    "Complete Katapult",
                    "Complete",
                    "Cancel",
                    "Cancel MRE",
                    "Cancel Katapult"
                ];
                // Filter out hidden statuses
                result.data = result.data.filter(item => !hidenStatuses.includes(item.name));
                const transformedData = result.data.map(item => ({
                    value: item.id,
                    label: item.name,
                }));
                setFilterOptions(transformedData);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching filter data:", error);
                setLoading(false);
            });
    }, []);

    // 2. Logika untuk memfilter opsi berdasarkan input pencarian
    const filteredOptions = useMemo(() => {
        return filterOptions.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, filterOptions]);

    // 3. Logika untuk "Select All"
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const allVisibleValues = filteredOptions.map(opt => opt.value);
            // Gabungkan dengan yang sudah dipilih sebelumnya untuk menghindari duplikat
            // @ts-ignore
            setSelectedValues(prev => [...new Set([...prev, ...allVisibleValues])]);
        } else {
            const allVisibleValues = filteredOptions.map(opt => opt.value);
            setSelectedValues(prev => prev.filter(val => !allVisibleValues.includes(val)));
        }
    };

    // 4. Efek untuk mengatur status checkbox "Select All" (checked / indeterminate)
    useEffect(() => {
        if (!selectAllRef.current) return;

        const visibleValues = filteredOptions.map(opt => opt.value);
        const selectedVisibleValues = visibleValues.filter(val => selectedValues.includes(val));

        if (selectedVisibleValues.length === 0) {
            selectAllRef.current.checked = false;
            selectAllRef.current.indeterminate = false;
        } else if (selectedVisibleValues.length === visibleValues.length) {
            selectAllRef.current.checked = true;
            selectAllRef.current.indeterminate = false;
        } else {
            selectAllRef.current.checked = false;
            selectAllRef.current.indeterminate = true;
        }
    }, [selectedValues, filteredOptions]);


    // ... sisa logika utama tidak banyak berubah ...
    useImperativeHandle(ref, () => ({
        isFilterActive: () => selectedValues.length > 0,
        doesFilterPass: (params: IDoesFilterPassParams) => {
            if (!selectedValues.length) return true;
            const cellValue = props.valueGetter(params as any);
            return selectedValues.includes(cellValue);
        },
        getModel: () => (selectedValues.length > 0 ? { values: selectedValues } : null),
        setModel: (model: FilterModel | null) => setSelectedValues(model ? model.values : []),
    }));

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        setSelectedValues(prev => checked ? [...prev, value] : prev.filter(v => v !== value));
    };

    useEffect(() => {
        props.filterChangedCallback();
    }, [selectedValues, props]);

    if (loading) {
        return <div className="custom-filter-container">Loading...</div>;
    }

    // 5. JSX yang diperbarui dengan Search bar dan "Select All"
    return (
        <div className="custom-filter-container">
            <div className="filter-search-wrapper">
                <input
                    type="text"
                    className="filter-search-input"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>
            <div className="filter-list">
                <div className="filter-item">
                    <label>
                        <input
                            type="checkbox"
                            ref={selectAllRef}
                            onChange={handleSelectAll}
                        />
                        (Select All)
                    </label>
                </div>
                {filteredOptions.map(option => (
                    <div className="filter-item" key={option.value}>
                        <label>
                            <input
                                type="checkbox"
                                value={option.value}
                                checked={selectedValues.includes(option.value)}
                                onChange={handleCheckboxChange}
                            />
                            {option.label}
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );
});

export default CheckboxFilterStatus;